import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import PrintDesignEditorHeader from './PrintDesignEditorHeader';
import PrintDesignEditorCanvasDisplay from './PrintDesignEditorCanvasDisplay';
import PrintDesignTemplateCreateModal from './PrintDesignTemplateCreateModal';
import PrintDesignEditorSidebar from './PrintDesignEditorSidebar';
import PrintDesignEditorFooter from './PrintDesignEditorFooter';
import {
  getDefaultPrintTemplates,
  useCanvasController
} from './helpers/fabricCanvasHelper';
import { usePrompt } from '../../../helpers/prompt';
import PrintDesignEditorSaveChangesModal from './PrintDesignEditorSaveChangesModal';
import { triggerNotification } from '../../../helpers/notification';
import { printDesignImagePositioningConfig } from './printDesignImagePositioningConfig';
import { groupVendorPrintDesignsByVariant } from '../../../helpers/vendorHelper';

const initialState = {
  selectedProductId: null,
  selectedProductDesignId: null,
  selectedVariantDesignId: null,
  fkEcomImagePosition: null,
  showTemplateCreateModal: false,
  showConfirmModal: false,
  showCanvasInfoCard: false,
  showVariantSelect: false,
  disableTemplateBackgroundColor: true,
  selectedColor: null,
  productDesigns: [],
  printPreview: {
    showPreview: false,
    previewImage: null,
    previewFile: null
  },
  templateLoading: false,
  loading: false
};

const PrintDesignEditor = ({
  productIds,
  ecomExternalProducts,
  printDesigns,
  fkEcomStore,
  fkEcomVendor,
  onSaveChanges,
  onSaveChangesComplete,
  onCreateDesigns,
  options,
  loading
}) => {
  const hasInitialized = useRef(null);
  const canvasRef = useRef(null);
  const canvasOverlayRef = useRef(null);
  const [toolbarMenuState, setToolbarMenuState] = useState({
    isOpen: false,
    action: null
  });
  const [formState, setFormState] = useState({ ...initialState });
  const [printTemplateState, setPrintTemplateState] = useState({
    id: null,
    productId: null,
    variantDesignId: null,
    fkEcomImagePosition: null,
    pkEcomStoreMedia: undefined,
    pkEcomVendorMedia: null,
    preview: null,
    printDesignPreview: null,
    currentHistoryIndex: -1,
    history: [],
    selectedObjects: [],
    loadingImageUrls: [],
    hasChanges: false,
    hasInitialized: false
  });
  const canvasController = useCanvasController(
    canvasRef,
    canvasOverlayRef,
    setPrintTemplateState
  );
  const {
    loadCanvasTemplate,
    createAndPopulateHiddenCanvasState
  } = canvasController;

  const hasChanges =
    printTemplateState.hasChanges ||
    formState.productDesigns.some((t) => t.hasChanges);

  const selectedExternalProduct = ecomExternalProducts.find(
    (p) => p.id === formState.selectedProductId
  );

  const defaultPrintTemplates = getDefaultPrintTemplates(
    selectedExternalProduct
  );
  const selectedPrintTemplate = defaultPrintTemplates.find(
    (t) =>
      t.fkEcomImagePosition.toString() ===
      printTemplateState.fkEcomImagePosition?.toString()
  );

  const isTemplateLoading =
    !hasInitialized.current ||
    loading ||
    formState.templateLoading ||
    printTemplateState.loadingImageUrls.length > 0;

  usePrompt(
    'Are you sure you want to leave this page? You have unsaved changes.',
    hasChanges && !formState.loading
  );

  useEffect(() => {
    if (!loading) {
      setFormState((prev) => ({
        ...prev,
        selectedProductId:
          ecomExternalProducts?.sort((a, b) => a.name.localeCompare(b.name))[0]
            ?.id || null,
        productDesigns:
          printDesigns?.map((d, index) => ({
            ...d,
            sort: index,
            variantDesigns: groupVendorPrintDesignsByVariant(d.designs),
            designs: null
          })) || []
      }));
    }
  }, [loading]);

  useEffect(() => {
    if (
      formState.selectedProductId &&
      !formState.templateLoading &&
      (!formState.selectedProductDesignId ||
        formState.selectedProductDesignId !== printTemplateState.id ||
        formState.selectedVariantDesignId !==
          printTemplateState.variantDesignId ||
        formState.fkEcomImagePosition !==
          printTemplateState.fkEcomImagePosition)
    ) {
      const defaultTemplate =
        defaultPrintTemplates.find(
          (t) =>
            t.fkEcomImagePosition.toString() ===
            formState.fkEcomImagePosition?.toString()
        ) || defaultPrintTemplates[0];

      const defaultPositioning = printDesignImagePositioningConfig.find(
        (config) =>
          (config.width === defaultTemplate?.imageWidthPx &&
            config.height === defaultTemplate?.imageHeightPx) ||
          (config.uuids && config.uuids.includes(selectedExternalProduct.uuid))
      )?.positioning?.[0];

      const selectedProductWithDesigns =
        formState.productDesigns.find(
          (t) => t.id === formState.selectedProductDesignId
        ) ||
        formState.productDesigns
          .filter((t) => t.productId === formState.selectedProductId)
          .sort((a, b) => a.sort - b.sort)[0];

      const selectedVariantDesign =
        selectedProductWithDesigns?.variantDesigns?.find(
          (d) => d.id === formState.selectedVariantDesignId
        ) ||
        selectedProductWithDesigns?.variantDesigns?.find((d) => d.isMainDesign);

      const selectedDesign = selectedVariantDesign?.designs.find(
        (d) =>
          d.fkEcomImagePosition.toString() ===
          defaultTemplate?.fkEcomImagePosition?.toString()
      );

      setFormState((prev) => ({
        ...prev,
        fkEcomImagePosition:
          defaultTemplate?.fkEcomImagePosition.toString() || null,
        selectedProductDesignId: selectedProductWithDesigns?.id || null,
        selectedVariantDesignId: selectedVariantDesign?.id || null,
        printPreview: {
          showPreview: false,
          previewImage: null,
          previewFile: null
        },
        templateLoading: true
      }));

      setPrintTemplateState((prev) => ({
        ...prev,
        fkEcomImagePosition:
          defaultTemplate?.fkEcomImagePosition.toString() || null,
        productId: formState.selectedProductId,
        printDesignPreview: selectedDesign?.printDesignPreview || null,
        history: [],
        currentHistoryIndex: (selectedDesign?.history?.length ?? 0) - 1,
        id: selectedProductWithDesigns?.id || null,
        ...selectedDesign,
        variantDesignId: selectedVariantDesign?.id || null,
        selectedObjects: [],
        loadingImageUrls: []
      }));

      loadCanvasTemplate(
        {
          templateImage: defaultTemplate?.svgSource,
          defaultPositioning,
          canvasState: selectedDesign?.canvasState || null,
          printImageUrl:
            selectedDesign && !selectedDesign.hasChanges
              ? selectedDesign?.printImageUrl || null
              : null
        },
        () => {
          setFormState((prev) => ({
            ...prev,
            templateLoading: false
          }));
        }
      );

      hasInitialized.current = true;
    }
  }, [
    formState.selectedProductId,
    formState.selectedProductDesignId,
    formState.selectedVariantDesignId,
    formState.fkEcomImagePosition
  ]);

  useEffect(() => {
    if (printTemplateState.id && printTemplateState.history.length > 1) {
      const hasExistingTemplate = formState.productDesigns.some(
        (t) =>
          t.productId === formState.selectedProductId &&
          t.id === printTemplateState.id
      );
      const externalProduct = ecomExternalProducts.find(
        (p) => p.id === formState.selectedProductId
      );

      if (!hasExistingTemplate && externalProduct) {
        const variantDesignId = new Date().getTime().toString();
        setFormState((prev) => ({
          ...prev,
          selectedProductDesignId: printTemplateState.id,
          selectedVariantDesignId: variantDesignId,
          productDesigns: [
            ...prev.productDesigns,
            {
              id: printTemplateState.id,
              productId: externalProduct.id,
              fkEcomExternalConnectionType:
                externalProduct.fkEcomExternalConnectionType,
              pkEcomStoreMedia: printTemplateState.pkEcomStoreMedia,
              pkEcomVendorMedia: printTemplateState.pkEcomVendorMedia,
              hasChanges: true,
              variantDesigns: [
                {
                  id: variantDesignId,
                  variantIds: [],
                  designs: [
                    {
                      ...printTemplateState,
                      canvasState:
                        printTemplateState.history[
                          printTemplateState.currentHistoryIndex
                        ]
                    }
                  ],
                  isMainDesign: true
                }
              ]
            }
          ]
        }));

        setPrintTemplateState((prev) => ({
          ...prev,
          variantDesignId
        }));
      }
    }
  }, [printTemplateState.id, printTemplateState?.history.length]);

  useEffect(() => {
    if (printTemplateState.id && printTemplateState.history.length > 1) {
      setFormState((prev) => ({
        ...prev,
        productDesigns: [
          ...prev.productDesigns.map((t) =>
            t.id === printTemplateState.id
              ? {
                  ...t,
                  variantDesigns: t.variantDesigns.map((vd) =>
                    vd.id === printTemplateState.variantDesignId
                      ? {
                          ...vd,
                          designs: [
                            ...vd.designs.filter(
                              (d) =>
                                d.fkEcomImagePosition !==
                                printTemplateState.fkEcomImagePosition
                            ),
                            {
                              ...printTemplateState,
                              canvasState:
                                printTemplateState.history[
                                  printTemplateState.currentHistoryIndex
                                ]
                            }
                          ]
                        }
                      : vd
                  ),
                  hasChanges: true
                }
              : t
          )
        ]
      }));
    }
  }, [printTemplateState.history[printTemplateState.currentHistoryIndex]]);

  const onAddNewTemplates = (
    { images, productIds: pIds, templateType, blankTemplateCount },
    onCallback
  ) => {
    const maxSort = Math.max(...formState.productDesigns.map((t) => t.sort), 0);
    if (templateType === 'images') {
      const newTemplates = pIds.flatMap((productId, index) => [
        ...images.map((image, imageIndex) => ({
          id: `${new Date().getTime().toString()}-${index}-${imageIndex}`,
          templateImage: image,
          preview: image.src,
          productId,
          sort: maxSort + index + 1,
          pkEcomVendorMedia: image.pkEcomVendorMedia,
          pkEcomStoreMedia: image.pkEcomStoreMedia
        }))
      ]);

      Promise.all(
        newTemplates.map(async (template) => {
          const externalProduct = ecomExternalProducts.find(
            (p) => p.id === template.productId
          );
          const defaultTemplates = getDefaultPrintTemplates(externalProduct);

          const templateConfig = defaultTemplates[0];

          const defaultPositioning = printDesignImagePositioningConfig.find(
            (config) =>
              (config.width === templateConfig?.imageWidthPx &&
                config.height === templateConfig?.imageHeightPx) ||
              (config.uuids && config.uuids.includes(externalProduct.uuid))
          )?.positioning?.[0];

          const {
            canvasState,
            imageUrl
          } = await createAndPopulateHiddenCanvasState(
            {
              templateImage: templateConfig?.svgSource,
              defaultPositioning,
              canvasState: null,
              imageUrl: template.templateImage.src
            },
            true
          );

          return {
            id: template.id,
            productId: externalProduct.id,
            fkEcomExternalConnectionType:
              externalProduct.fkEcomExternalConnectionType,
            hasChanges: true,
            printDesignPreview: imageUrl,
            pkEcomStoreMedia: template.pkEcomStoreMedia,
            pkEcomVendorMedia: template.pkEcomVendorMedia,
            variantDesigns: [
              {
                id: template.id,
                label: 'Main design',
                variantIds: [],
                designs: [
                  {
                    ...canvasState,
                    ...template,
                    canvasState,
                    history: [canvasState],
                    fkEcomExternalConnectionType:
                      externalProduct.fkEcomExternalConnectionType,
                    fkEcomImagePosition: templateConfig?.fkEcomImagePosition,
                    hasChanges: true
                  }
                ],
                isMainDesign: true
              }
            ]
          };
        })
      )
        .then((templates) => {
          setFormState({
            ...formState,
            selectedProductDesignId:
              formState.selectedProductDesignId ||
              templates
                .filter((t) => t.productId === formState.selectedProductId)
                .sort((a, b) => a.sort - b.sort)[0]?.id ||
              null,
            showTemplateCreateModal: false,
            productDesigns: [...formState.productDesigns, ...templates]
          });
        })
        .catch(() => {
          triggerNotification(
            'Failed to create templates from selected image(s). Please try again or select different a image.'
          );
          onCallback();
        });
    }
    else {
      const blankTemplates = pIds.flatMap((productId, index) =>
        Array.from({ length: blankTemplateCount }, (_, i) => {
          const externalProduct = ecomExternalProducts.find(
            (p) => p.id === productId
          );

          return {
            id: `${Date.now()}-${productId}-${i}`,
            productId,
            fkEcomExternalConnectionType:
              externalProduct.fkEcomExternalConnectionType,
            sort: maxSort + index + 1
          };
        })
      );

      setFormState({
        ...formState,
        selectedProductDesignId:
          formState.selectedProductDesignId ||
          blankTemplates
            .filter((t) => t.productId === formState.selectedProductId)
            .sort((a, b) => a.sort - b.sort)[0]?.id ||
          null,
        showTemplateCreateModal: false,
        productDesigns: [...formState.productDesigns, ...blankTemplates]
      });
    }
  };

  const onSwitchPrintImagePosition = (fkEcomImagePosition) => {
    setFormState((prev) => ({
      ...prev,
      fkEcomImagePosition
    }));
  };

  return (
    <AppStack
      style={{
        flex: 1,
        gap: 0,
        width: '100%',
        height: '100%',
        maxHeight: 'calc(100vh - 60px)',
        overflow: 'hidden'
      }}
    >
      <PrintDesignEditorHeader
        canvasController={canvasController}
        ecomExternalProducts={ecomExternalProducts}
        fkEcomStore={fkEcomStore}
        fkEcomVendor={fkEcomVendor}
        formState={formState}
        hasChanges={hasChanges}
        printTemplateState={printTemplateState}
        setFormState={setFormState}
        setPrintTemplateState={setPrintTemplateState}
        setToolbarMenuState={setToolbarMenuState}
        toolbarMenuState={toolbarMenuState}
      />

      <AppFlexbox
        style={{
          flex: 1,
          gap: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden'
        }}
      >
        <PrintDesignEditorSidebar
          canvasController={canvasController}
          ecomExternalProducts={ecomExternalProducts}
          fkEcomStore={fkEcomStore}
          fkEcomVendor={fkEcomVendor}
          formState={formState}
          loading={isTemplateLoading}
          printTemplateState={printTemplateState}
          productIds={productIds}
          setFormState={setFormState}
          setPrintTemplateState={setPrintTemplateState}
          setToolbarMenuState={setToolbarMenuState}
          toolbarMenuState={toolbarMenuState}
        />

        <AppStack
          style={{
            flex: 1,
            gap: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden'
          }}
        >
          <PrintDesignEditorCanvasDisplay
            canvasController={canvasController}
            canvasOverlayRef={canvasOverlayRef}
            canvasRef={canvasRef}
            ecomExternalProduct={selectedExternalProduct}
            formState={formState}
            loading={isTemplateLoading}
            onSwitchPrintImagePosition={onSwitchPrintImagePosition}
            printTemplate={selectedPrintTemplate}
            printTemplateState={printTemplateState}
            setFormState={setFormState}
            setToolbarMenuState={setToolbarMenuState}
            toolbarMenuState={toolbarMenuState}
          />

          <PrintDesignEditorFooter
            canvasController={canvasController}
            ecomExternalProducts={ecomExternalProducts}
            fkEcomStore={fkEcomStore}
            fkEcomVendor={fkEcomVendor}
            formState={formState}
            printTemplateState={printTemplateState}
            setFormState={setFormState}
            setPrintTemplateState={setPrintTemplateState}
          />
        </AppStack>
      </AppFlexbox>

      <PrintDesignTemplateCreateModal
        ecomExternalProducts={ecomExternalProducts}
        fkEcomStore={fkEcomStore}
        fkEcomVendor={fkEcomVendor}
        isOpen={formState.showTemplateCreateModal}
        onClose={() =>
          setFormState({ ...formState, showTemplateCreateModal: false })
        }
        onConfirm={onAddNewTemplates}
      />

      <PrintDesignEditorSaveChangesModal
        canvasController={canvasController}
        ecomExternalProducts={ecomExternalProducts}
        fkEcomStore={fkEcomStore}
        fkEcomVendor={fkEcomVendor}
        formState={formState}
        onCreateDesigns={onCreateDesigns}
        onSaveChanges={onSaveChanges}
        onSaveChangesComplete={onSaveChangesComplete}
        options={options}
        printTemplateState={printTemplateState}
        setFormState={setFormState}
        setPrintTemplateState={setPrintTemplateState}
      />
    </AppStack>
  );
};

PrintDesignEditor.propTypes = {
  ecomExternalProducts: PropTypes.array.isRequired,
  onCreateDesigns: PropTypes.func.isRequired,
  onSaveChanges: PropTypes.func.isRequired,
  fkEcomStore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fkEcomVendor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loading: PropTypes.bool,
  onSaveChangesComplete: PropTypes.func,
  options: PropTypes.object,
  printDesigns: PropTypes.array,
  productDesigns: PropTypes.array,
  productIds: PropTypes.array
};

export default PrintDesignEditor;
